<template>
  <div class="config-image__area clearfix">
    <div :class="titleBarClasses">
      <div class="row">
        <div class="col-xs-12">
          <div class="photobook-group__header">
            <span
              v-if="!groupIsValid && showValidation"
              class="photoset__fail"
            >!</span>
            <contentful-text
              :item="contentfulText('photo-book-group-header')"
              :data="{ numPhotos: optionProduct.FramesRequired }"
              :apply-markdown="true"
              :render-inline="true"
              :class="groupHeaderContentClasses"
            />
          </div>
        </div>
      </div>
    </div>
    <photo-selection-selected-frame
      v-for="(selectedFrame, selectionSlotNumber) in selectedFrames"
      :selected-frame-id="selectedFrame.frameId"
      :quantity="selectedFrame.quantity"
      :allow-quantity-update="false"
      :key="`selection-${optionProduct.CatalogPackageOptionProductID}-${selectionSlotNumber}`"
      :option-product="optionProduct"
      @change-photo-clicked="handleSelectPhotoClicked(selectionSlotNumber)"
      @remove-clicked="handleRemoveClicked(selectionSlotNumber)"
      @quantity-updated="handleQuantityUpdated($event, selectionSlotNumber)"
    />
    <photo-selection-placeholder
      v-for="remainingEmptyFramesIndex in remainingEmptyFramesCount"
      :key="`placeholder-${optionProduct.CatalogPackageOptionProductID}-${remainingEmptyFramesIndex}`"
      :is-next-selection="remainingEmptyFramesIndex == 1"
      :class="placeholderClasses"
      @select-photo-clicked="handleSelectPhotoClicked((selectedFrames.length + (remainingEmptyFramesIndex - 1)))"
    />
    <select-photo-modal
      :show="showSelectPhotoModal"
      @close="showSelectPhotoModal = false"
      @photo-selected="handlePhotoSelected"
    />
  </div>
</template>

<script>
import Vue from 'vue'
import PhotoSelectionSelectedFrame from '@/default/catalog-mix/components/PhotoSelection/Slot/SelectedFrame'
import PhotoSelectionPlaceholder from '@/default/catalog-mix/components/PhotoSelection/Slot/Placeholder'
import SelectPhotoModal from '@Shared/components/catalog/SelectPhotoModal/SelectPhotoModal'

export default {
  name: 'PhotoBookGroup',
  components: {
    PhotoSelectionSelectedFrame,
    PhotoSelectionPlaceholder,
    SelectPhotoModal
  },
  props: {
    optionProduct: { type: Object, default: null, required: true },
    showValidation: { type: Boolean, default: false, required: true }
  },
  data () {
    return {
      showSelectPhotoModal: false,
      activeSlotNumber: null,
      selectedFrames: []

    }
  },
  computed: {
    titleBarClasses () {
      return {
        'config-image__title-bar': true,
        'config-image__title-bar--alt-gradimages': true,
        'config-image__title-bar--invalid': !this.groupIsValid && this.showValidation,
        'photobook-group__title-bar': true
      }
    },
    groupIsValid () {
      return this.remainingEmptyFramesCount === 0
    },
    remainingEmptyFramesCount () {
      const numSelectedFrames = this.selectedFrames.length
      const numRequiredFrames = this.optionProduct.FramesRequired

      return numRequiredFrames - numSelectedFrames
    },
    alwaysShowRetouchingModal () {
      return this.$store.state.catalogPageCommon.alwaysShowRetouchingModal
    },
    placeholderClasses () {
      return {
        'placeholder--group-has-selection': this.remainingEmptyFramesCount !== this.optionProduct.FramesRequired && (this.allowRetouching === false && this.allowCropping === false),
        'placeholder--group-has-selection-long': this.remainingEmptyFramesCount !== this.optionProduct.FramesRequired && (this.allowRetouching === true || this.allowCropping === true)
      }
    },
    allowCropping () {
      return this.$store.state.catalogPageCommon.allowCropping
    },
    allowRetouching () {
      return this.$store.state.catalogPageCommon.allowRetouching
    },
    groupHeaderContentClasses () {
      return {
        'photobook-group__header-content--validation-visible': this.showValidation === true
      }
    },
    content () {
      return this.$store.state.photoBookModuleContent
    }
  },
  watch: {
    selectedFrames (v) {
      this.$emit('selected-frames-updated', { selectedFrames: this.selectedFrames })
    }
  },
  methods: {
    handleSelectPhotoClicked (selectionSlotNumber) {
      this.activeSlotNumber = selectionSlotNumber
      this.showSelectPhotoModal = true
    },
    handlePhotoSelected (data) {
      const existingSlotEntry = this.selectedFrames[this.activeSlotNumber]

      if (existingSlotEntry === undefined) {
        this.selectedFrames.push({
          frameId: data.frameInfo.FrameID,
          quantity: 1
        })
      } else {
        Vue.set(this.selectedFrames, this.activeSlotNumber, {
          frameId: data.frameInfo.FrameID,
          quantity: 1
        })
      }

      if (this.allowRetouching === true && this.alwaysShowRetouchingModal === true) {
        document.dispatchEvent(new CustomEvent('retouchingModal.show', {
          detail: { frameId: data.frameInfo.FrameID, alwaysShow: false }
        }))
      }

      this.activeSlotNumber = null
    }
  }
}
</script>

<style scoped lang="scss">
.photobook-group {
  &__header {
    display: inline-block;
    font-family: "Open Sans", sans-serif;
    font-size: 14px;
    line-height: 0;
    margin-right: 5px;

    &-content {
      &--validation-visible {
        position: relative;
        left: 10px;
      }
    }
  }

  @media screen and (min-width: $screen-sm-min) {
    &__header {
      font-size: 16px;
    }

    &__title-bar {
      width: 335px; // this manually sets the width of this element so that its border lines up with a 3 photo photo book config. needs to be reworked if supporting another number of photos.
    }
  }

  @media screen and (min-width: $screen-md-min) {
    &__title-bar {
      width: 544px; // this manually sets the width of this element so that its border lines up with a 3 photo photo book config. needs to be reworked if supporting another number of photos.
    }
  }
}

.placeholder--group-has-selection {
  height: 254px;

  &-long {
    height: 278px;
  }

  @media screen and (min-width: $screen-sm-min) {
    height: 378px;
  }

  @media screen and (min-width: $screen-md-min) {
    height: 278px;
  }

  @media screen and (min-width: $screen-lg-min) {
    height: 325px;
  }
}
</style>



// WEBPACK FOOTER //
// WebResources/vue/default/catalog-details/components/PhotoBook/Group.vue